<template>
  <div class="asa-data-export">
    <h3>ASA数据导出</h3>

    <el-form
      :inline="true"
      :model="exportForm"
      style="width: 100%"
      label-position="right"
      label-width="auto"
    >
      <el-row :gutter="20">
        <el-col :span="8">
          <el-form-item label="时间范围:" style="width: 100%">
            <el-date-picker
              v-model="exportForm.date"
              type="daterange"
              unlink-panels
              size="default"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              :shortcuts="shortcuts"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              :disabled-date="disabledDate"
              @change="handleDateChange"
              style="height: 32px; width: 100%"
            />
          </el-form-item>
        </el-col>

        <el-col :span="5">
          <el-form-item label="项目:" style="width: 100%">
            <el-select
              v-model="exportForm.p"
              placeholder="选择项目"
              filterable
              allow-create
              style="width: 100%"
            >
              <el-option
                v-for="item in project_options"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
        </el-col>

        <el-col :span="6">
          <el-form-item label="发送到邮箱:" style="width: 100%">
            <el-checkbox v-model="exportForm.sendToEmail" style="margin-right: 10px">
              <span>启用</span>
              <el-tooltip content="勾选后，数据将在后台生成并自动发送到指定邮箱，您可以选择关闭页面" placement="top">
                <el-icon style="margin-left: 4px; color: #909399; cursor: help;">
                  <InfoFilled />
                </el-icon>
              </el-tooltip>
            </el-checkbox>
            <el-input
              v-model="exportForm.email"
              placeholder="请输入邮箱地址"
              :disabled="!exportForm.sendToEmail"
              style="width: calc(100% - 80px)"
              clearable
            >
              <template #prefix>
                <el-icon><Message /></el-icon>
              </template>
            </el-input>
          </el-form-item>
        </el-col>

        <el-col :span="5">
          <el-form-item>
            <el-button
              type="primary"
              @click="export_asa_data"
              :loading="exporting"
              :disabled="exporting"
              style="width: 200px"
            >
              {{ exporting ? '导出中...' : (exportForm.sendToEmail ? '发送到邮箱' : '导出数据') }}
            </el-button>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>

        <div class="remark-text" style="margin-bottom: 5px">
      <el-tooltip class="box-item" effect="dark" placement="top-start" width="500">
        <template #content>
          <div style="line-height: 1.8; padding: 5px;">
            <strong>📋 数据导出说明：</strong>
            <ol style="margin: 8px 0; padding-left: 20px;">
              <li>数据导出包含系列数据和关键词数据</li>
              <li>每天的数据将分别放在不同的工作表中</li>
              <li>工作表命名格式：日期_系列 和 日期_关键词</li>
              <li>Excel文件命名格式：asa_开始日期_结束日期_项目.xlsx</li>
            </ol>
            <strong>💡 导出模式说明：</strong>
            <ul style="margin: 8px 0; padding-left: 20px;">
              <li><strong>直接下载模式</strong>：日期≤30天，数据实时生成并下载，请勿关闭页面</li>
              <li><strong>邮箱发送模式</strong>：日期>30天或勾选邮箱选项，数据在后台生成，完成后自动发送到邮箱，可关闭页面</li>
            </ul>
            <strong>⚠️ 注意事项：</strong>
            <ul style="margin: 8px 0; padding-left: 20px;">
              <li>最大支持导出90天数据</li>
              <li>日期超过30天将自动启用邮箱发送模式</li>
              <li>邮箱发送模式下，请检查垃圾邮件文件夹</li>
            </ul>
          </div>
        </template>
        <el-button style="color: #409EFF; border: none; background: none; padding: 0;">
          <span style="display: flex; align-items: center;">
            <el-icon style="margin-right: 4px;"><InfoFilled /></el-icon>
            使用说明
          </span>
        </el-button>
      </el-tooltip>
    </div>

    <el-alert
      v-if="exportProgress.show"
      :title="exportProgress.message"
      :type="exportProgress.type"
      :description="exportProgress.description"
      show-icon
      :closable="false"
      style="margin-top: 20px; white-space: pre-line;"
    >
      <template #default>
        <div style="white-space: pre-line; line-height: 1.6;">{{ exportProgress.description }}</div>
      </template>
    </el-alert>

    <div v-if="exportProgress.show && exportProgress.currentDate" style="margin-top: 20px">
      <el-progress
        :percentage="exportProgress.percentage"
        :status="exportProgress.status"
      >
        <span class="percentage-value">{{ exportProgress.percentage }}%</span>
      </el-progress>
      <p style="margin-top: 10px; color: #606266">
        正在处理日期: {{ exportProgress.currentDate }} ({{ exportProgress.currentIndex }}/{{ exportProgress.total }})
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { export_asa_data_excel } from '../services/requests'
import { ElMessage } from 'element-plus'
import { InfoFilled, Message } from '@element-plus/icons-vue'

// 项目选项
const project_options = [
  { label: 'MetroVPN', value: 'MetroVPN' },
  { label: 'PDFConverter', value: 'PDFConverter' },
  { label: 'Pixeta', value: 'Pixeta' },
  { label: 'LineVPN', value: 'LineVPN' }
]

// 日期快捷选项
const shortcuts = [
  {
    text: '最近一周',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
      return [start, end]
    }
  },
  {
    text: '最近一个月',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
      return [start, end]
    }
  },
  {
    text: '最近三个月',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
      return [start, end]
    }
  }
]

// 导出表单
const exportForm = reactive({
  date: [],
  p: 'PDFConverter',
  sendToEmail: false,
  email: ''
})

// 导出状态
const exporting = ref(false)
const exportProgress = reactive({
  show: false,
  message: '',
  type: 'info',
  description: ''
})

// 常量
const MAX_DAYS = 90  // 最大日期范围：3个月
const FORCE_EMAIL_THRESHOLD = 30  // 强制邮箱阈值：1个月

// 初始化日期范围为最近一周
onMounted(() => {
  const end = new Date()
  const start = new Date()
  start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
  exportForm.date = [
    formatDate(start),
    formatDate(end)
  ]
})

// 格式化日期
function formatDate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// 计算日期天数
function calculateDays(startDate, endDate) {
  const start = new Date(startDate)
  const end = new Date(endDate)
  const diffTime = Math.abs(end - start)
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
}

// 禁用日期（限制最大3个月）
function disabledDate(time) {
  const selectedStart = exportForm.date && exportForm.date[0] ? new Date(exportForm.date[0]) : null
  const date = new Date(time)
  
  if (selectedStart) {
    // 如果已选择开始日期，限制结束日期最多90天后
    const maxEndDate = new Date(selectedStart)
    maxEndDate.setDate(maxEndDate.getDate() + MAX_DAYS - 1)
    return date > maxEndDate
  }
  
  return false
}

// 日期变化监听
function handleDateChange() {
  if (exportForm.date && exportForm.date.length === 2) {
    const days = calculateDays(exportForm.date[0], exportForm.date[1])
    
    if (days > FORCE_EMAIL_THRESHOLD) {
      // 日期超过1个月，自动勾选邮箱
      exportForm.sendToEmail = true
    }
  }
}

// 导出ASA数据
async function export_asa_data() {
  // 1. 验证日期
  if (!exportForm.date || exportForm.date.length !== 2) {
    ElMessage.error('请选择日期范围')
    return
  }

  // 2. 验证项目
  if (!exportForm.p) {
    ElMessage.error('请选择项目')
    return
  }

  // 3. 验证日期范围
  const days = calculateDays(exportForm.date[0], exportForm.date[1])
  if (days > MAX_DAYS) {
    ElMessage.error(`日期范围不能超过3个月（${MAX_DAYS}天）`)
    return
  }

  // 4. 验证邮箱（如果日期超过1个月）
  if (days > FORCE_EMAIL_THRESHOLD) {
    if (!exportForm.sendToEmail) {
      ElMessage.warning({
        message: '⚠️ 日期范围超过30天，建议勾选"发送到邮箱"以确保数据完整导出',
        duration: 4000
      })
      exportForm.sendToEmail = true  // 自动勾选邮箱选项
    }
    if (!exportForm.email || exportForm.email.trim() === '') {
      ElMessage.error({
        message: '请输入接收文件的邮箱地址',
        duration: 3000
      })
      return
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(exportForm.email)) {
      ElMessage.error({
        message: '邮箱地址格式不正确，请检查（如：example@domain.com）',
        duration: 3000
      })
      return
    }
  }

  // 5. 验证邮箱（如果勾选了发送到邮箱）
  if (exportForm.sendToEmail) {
    if (!exportForm.email || exportForm.email.trim() === '') {
      ElMessage.error({
        message: '请输入接收文件的邮箱地址',
        duration: 3000
      })
      return
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(exportForm.email)) {
      ElMessage.error({
        message: '邮箱地址格式不正确，请检查（如：example@domain.com）',
        duration: 3000
      })
      return
    }
  }

  exporting.value = true
  
  // 立即显示进度信息
  exportProgress.show = true
  exportProgress.message = '开始处理...'
  exportProgress.type = 'info'
  
  const start_date = exportForm.date[0]
  const end_date = exportForm.date[1]
  const p = exportForm.p
  const email = exportForm.sendToEmail ? exportForm.email.trim() : ''
  
  // 估算预计时间
  const estimatedMinutes = days > 60 ? '10-20' : (days > 30 ? '5-10' : '1-3')
  
  if (exportForm.sendToEmail) {
    // 邮箱模式
    exportProgress.description = `正在创建后台导出任务...\n\n📋 任务信息：\n• 日期范围：${start_date} 至 ${end_date}（共${days}天）\n• 项目：${p}\n• 接收邮箱：${email}\n• 预计耗时：约${estimatedMinutes}分钟\n\n✨ 温馨提示：数据将在后台生成，完成后会自动发送到您的邮箱。您可以选择关闭此页面，不会影响任务执行。`
  } else {
    // 下载模式
    exportProgress.description = `正在生成Excel文件，日期范围：${start_date} 至 ${end_date}（共${days}天），项目：${p}。预计需要${estimatedMinutes}分钟，请勿关闭页面。`
  }

  try {
    // 调用统一导出接口
    const response = await export_asa_data_excel({
      start_date: start_date,
      end_date: end_date,
      p: p,
      email: email
    })

    // 检查响应数据类型
    const responseData = response.data
    
    // 判断是否为JSON对象（说明返回的是错误或任务信息）
    if (typeof responseData === 'object' && responseData !== null && 'code' in responseData) {
      // 返回的是JSON响应（后台任务模式或错误）
      exportProgress.message = '正在创建后台任务...'
      exportProgress.type = 'info'
      
      const jsonData = responseData

      if (jsonData.code === 200) {
        // 后台任务创建成功
        exportProgress.message = '任务创建成功！'
        exportProgress.type = 'success'
        
        const estimatedTime = days > 60 ? '10-20' : '5-10'
        exportProgress.description = `✅ 后台任务已成功创建！\n\n📬 数据将在后台生成并发送到邮箱：${email}\n\n⏱️ 预计耗时：约${estimatedTime}分钟\n\n💡 提示：\n• 数据生成过程中，您可以关闭此页面，不会影响任务执行\n• 完成后Excel文件将自动发送到您的邮箱\n• 请留意收件箱（可能需要检查垃圾邮件文件夹）\n• 如${estimatedTime}分钟后未收到邮件，请稍后重试或联系管理员`
        
        ElMessage.success({
          message: `任务已创建！文件将在${estimatedTime}分钟后发送到 ${email}`,
          duration: 5000
        })
      } else {
        // 接口返回错误码
        const errorMsg = jsonData.message || '创建任务失败，请稍后重试'
        exportProgress.message = '任务创建失败'
        exportProgress.type = 'error'
        exportProgress.description = `⚠️ ${errorMsg}`
        
        ElMessage.error({
          message: errorMsg,
          duration: 5000,
          showClose: true
        })
      }
    } else {
      // 返回的是Excel文件（同步模式）
      exportProgress.message = '正在准备下载文件...'
      exportProgress.type = 'info'
      
      const blob = new Blob([responseData], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const filename = `asa_${start_date}_${end_date}_${p}.xlsx`

      // 创建下载链接
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)

      exportProgress.message = '导出成功'
      exportProgress.type = 'success'
      exportProgress.description = `文件已成功下载: ${filename}，包含${days}天的数据（${start_date} 至 ${end_date}）`
      ElMessage.success('数据导出成功')
    }
  } catch (error) {
    console.error('导出失败:', error)
    
    // 提取错误消息
    let errorMessage = '数据导出过程中发生错误，请稍后重试'
    
    if (error.response) {
      // 有响应的错误
      const responseData = error.response.data
      
      // 检查是否为JSON格式的错误响应
      if (typeof responseData === 'object' && responseData !== null && responseData.message) {
        errorMessage = responseData.message
      } else if (typeof responseData === 'string') {
        // 如果是字符串，直接使用
        errorMessage = responseData
      } else {
        // 其他情况，使用默认消息
        errorMessage = error.response.statusText || errorMessage
      }
    } else if (error.message) {
      // 网络错误或其他错误
      errorMessage = error.message
    }
    
    // 优化错误消息，使其更加友好
    if (errorMessage.includes('日期范围不能超过')) {
      errorMessage = `⚠️ ${errorMessage}\n\n💡 建议：请缩短日期范围或勾选"发送到邮箱"选项。`
    } else if (errorMessage.includes('必须勾选') || errorMessage.includes('必须提供邮箱')) {
      errorMessage = `⚠️ ${errorMessage}\n\n💡 说明：日期范围超过30天的数据量较大，使用邮箱发送可以避免浏览器超时，确保数据完整导出。`
    } else if (errorMessage.includes('邮箱地址') || errorMessage.includes('email') || errorMessage.includes('邮箱')) {
      errorMessage = `⚠️ ${errorMessage}\n\n💡 请检查：\n• 邮箱地址格式是否正确（如：example@domain.com）\n• 邮箱地址是否包含空格或特殊字符`
    } else if (errorMessage.includes('网络') || errorMessage.includes('timeout') || errorMessage.includes('超时')) {
      errorMessage = `⚠️ 网络连接超时或失败\n\n💡 建议：\n• 检查网络连接是否正常\n• 刷新页面后重试\n• 如果问题持续，请稍后再试`
    }
    
    exportProgress.message = exportForm.sendToEmail ? '任务创建失败' : '导出失败'
    exportProgress.type = 'error'
    exportProgress.description = errorMessage
    
    // 显示详细的错误信息
    ElMessage.error({
      message: errorMessage,
      duration: 6000,
      showClose: true
    })
  } finally {
    exporting.value = false
  }
}
</script>

<style scoped>
.asa-data-export {
  padding: 20px;
}

.remark-text {
  margin-bottom: 15px;
}

.percentage-value {
  font-size: 14px;
  color: #606266;
}

:deep(.el-progress-bar__innerText) {
  display: none;
}
</style>